import { UrlConfig } from './../../../../../api';
import {
    AnyJson,
    Consumer,
    createCache,
    hashString,
    hashToIdentifier,
    isFunction,
    longHash,
    noop,
    urlToString,
} from './../../../../../utils';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'SdkJsonp';

export interface SdkJsonpDependencies {

    urlsService: UrlConfig;
}

export type JsonpCallback = Consumer<AnyJson>;

export interface JsonpCallbacks {
    [url: string]: JsonpCallback;
}

/*
 * Cache used to improve the performance of digest computation
 */
const _digestCache = createCache<string>();

/*
 * Computes a digest based on a string token
 *
 * @param aToken    the token
 * @return the digest
 */
const _createDigest = (aToken: string): string => hashToIdentifier(hashString(longHash(), aToken));

/*
 * Computes a digest based on a string token
 *
 * @param aToken    the token
 * @return the digest
 */
const _getHash = (aToken: string): string => _digestCache(aToken, _createDigest);

/*
 * Registers a new callback with a URL and returns the name of the callback function.
 *
 * @param aApiRoot      the root URL
 * @param aUrl          the URL
 * @param aCallback     the callback function to register
 * @param aCallbacks    the set of callbacks
 */
function _registerCallback(aApiRoot: string, aUrl: string, aCallback: JsonpCallback, aCallbacks: JsonpCallbacks) {
    // check if the URL starts with the API root, if so, strip it off
    const url = aUrl.startsWith(aApiRoot) ? aUrl.substr(aApiRoot.length) : aUrl;
    // check if we have a clash on the digest
    let suffix = 0;
    const baseDigest = _getHash(url);
    let digest = baseDigest;
    while (isFunction(aCallbacks[digest])) {
        // next
        digest = baseDigest + suffix++;
    }
    // register the new digest
    aCallbacks[digest] = aCallback;
    return digest;
}

/*
 * Removes the callback associated with this digest
 *
 * @param aDigest   the digest
 * @param aCallbacks    the callback record
 */
const _removeCallback = (aDigest: string, aCallbacks: JsonpCallbacks): boolean => delete aCallbacks[aDigest];

/*
 * Encapsulation of the JSONP functions of the SDK. We expose them on the window object
 * since the JSONP callbacks rely on the object, anyway.
 */
export class SdkJsonp {

    /*
     * The currently registered callbacks. This field will be used
     * by the actual JSONP call.
     */
    callback: JsonpCallbacks;

    /*
     * Registers a new callback with a URL and returns the name of the callback function.
     *
     * @param aUrl the URL
     * @param aCallback the callback to register
     * @return the callback function
     */
    addCallback: (aUrl: string, aCallback: JsonpCallback) => string;

    /*
     * Removes the callback registered for this digest
     *
     * @param aDigest   the digest
     * @return true if the callback has been unregistered, else false
     */
    removeCallback: (aDigest: string) => boolean;

    /*
     * Some optional cleanup logic
     */
    destroy: () => void;

    constructor(aDeps: SdkJsonpDependencies) {
        // this pointer
        const that = this;

        // init the callback
        const callback = that.callback = {};

        // API base URL
        const apiRoot = urlToString(aDeps.urlsService.apiUrl);

        const _thisAddCallback = (aUrl: string, aCallback: JsonpCallback): string => _registerCallback(apiRoot, aUrl, aCallback, callback);
        const _thisRemoveCallback = (aDigest: string): boolean => _removeCallback(aDigest, callback);

        // init the methods
        that.addCallback = _thisAddCallback;
        that.removeCallback = _thisRemoveCallback;
        that.destroy = noop;
    }
}
